<template>
  <el-container class="shell">
    <el-aside class="sidebar" width="240px">
      <div class="brand">
        <span class="brand-mark">庆</span>
        <span>庆喜儿运营后台</span>
      </div>
      <el-scrollbar class="menu-scroll">
        <el-menu
          class="sidebar-menu"
          router
          :default-active="route.path"
          background-color="transparent"
          text-color="#cbd5e1"
          active-text-color="#ffffff"
        >
          <AdminMenuTree :items="menuTree" />
        </el-menu>
      </el-scrollbar>
    </el-aside>
    <el-container>
      <el-header>
        <span class="header-title">系统管理与业务运营</span>
        <div class="account">
          <el-dropdown trigger="click" @command="handleAccountCommand">
            <button class="account-trigger" type="button">
              <span class="account-avatar">{{ accountInitial }}</span>
              <span>{{ accountName }}</span>
              <el-icon><ArrowDown /></el-icon>
            </button>
            <template #dropdown>
              <el-dropdown-menu>
                <el-dropdown-item command="profile">
                  <el-icon><User /></el-icon>
                  个人中心
                </el-dropdown-item>
                <el-dropdown-item command="logout" divided :disabled="loggingOut">
                  <el-icon><SwitchButton /></el-icon>
                  {{ loggingOut ? '正在退出' : '退出登录' }}
                </el-dropdown-item>
              </el-dropdown-menu>
            </template>
          </el-dropdown>
        </div>
      </el-header>
      <el-main><router-view /></el-main>
    </el-container>
  </el-container>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import { ArrowDown, SwitchButton, User } from '@element-plus/icons-vue';
import { useRoute, useRouter } from 'vue-router';
import AdminMenuTree, { type MenuNode } from '@/components/AdminMenuTree.vue';
import { useAdminStore } from '@/stores/admin';

const router = useRouter();
const route = useRoute();
const store = useAdminStore();
const loggingOut = ref(false);
const accountName = computed(() => store.identity?.displayName || store.identity?.username || '账户');
const accountInitial = computed(() => accountName.value.trim().slice(0, 1));
const menuTree = computed<MenuNode[]>(() => {
  const menus = store.identity?.menus ?? [];
  const nodes = new Map(menus.map(menu => [menu.id, { ...menu, children: [] as MenuNode[] }]));
  const roots: MenuNode[] = [];
  nodes.forEach(node => {
    const parent = node.parentId ? nodes.get(node.parentId) : undefined;
    if (parent) parent.children.push(node);
    else roots.push(node);
  });
  const sort = (items: MenuNode[]): MenuNode[] => items
    .sort((left, right) => left.sort - right.sort)
    .map(item => ({ ...item, children: sort(item.children) }));
  return sort(roots);
});

async function logout() {
  loggingOut.value = true;
  try {
    await store.logout();
    await router.replace('/login');
  } finally {
    loggingOut.value = false;
  }
}

async function handleAccountCommand(command: string | number | object) {
  if (command === 'profile') await router.push('/profile');
  if (command === 'logout') await logout();
}
</script>

<style scoped>
.shell {
  min-height: calc(100vh - var(--qx-safe-top) - var(--qx-safe-bottom));
  background: var(--qx-page-bg);
}

.sidebar {
  background: var(--qx-sidebar-bg);
  color: var(--qx-sidebar-text-active);
  box-shadow: 4px 0 18px rgb(15 23 42 / 8%);
}

.brand {
  display: flex;
  align-items: center;
  gap: 12px;
  min-height: 72px;
  padding: 16px 20px;
  border-bottom: 1px solid rgb(148 163 184 / 14%);
  font-size: 18px;
  font-weight: 700;
  letter-spacing: .02em;
}

.brand-mark {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  flex: 0 0 34px;
  border-radius: 10px;
  background: var(--qx-brand);
  color: #fff;
  font-size: 18px;
  box-shadow: 0 8px 18px rgb(37 99 235 / 28%);
}

.menu-scroll {
  height: calc(100vh - var(--qx-safe-top) - var(--qx-safe-bottom) - 72px);
  padding: 10px 0 18px;
}

.el-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 64px;
  padding: 0 24px;
  background: var(--qx-panel-bg);
  border-bottom: 1px solid var(--qx-border);
}

.header-title {
  color: var(--qx-text-heading);
  font-weight: 600;
}

.account {
  display: flex;
  align-items: center;
  color: var(--qx-text-secondary);
}

.account-trigger {
  display: inline-flex;
  align-items: center;
  gap: 9px;
  min-height: 40px;
  padding: 4px 8px 4px 5px;
  border: 0;
  border-radius: 10px;
  background: transparent;
  color: var(--qx-text-primary);
  font: inherit;
  cursor: pointer;
  transition: background-color .18s ease;
}

.account-trigger:hover,
.account-trigger:focus-visible {
  background: #f1f5f9;
  outline: none;
}

.account-avatar {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 9px;
  background: #e8eefc;
  color: var(--qx-brand);
  font-weight: 700;
}

.el-main {
  background: var(--qx-page-bg);
  padding: 24px;
}
</style>
